import { useCallback, useEffect, useState } from 'react'
import { isClientSide } from '../clientEnv/isClientSide'
import { StorageKeys, StorageKeysEnum } from './storageKeys'

type UseStateStorageParams<T> = {
  key: StorageKeysEnum
  parser: (value: unknown) => T
}

export const clearClientStateStorage = () => {
  if (isClientSide()) {
    Object.values(StorageKeys).forEach((key) => {
      window.sessionStorage.removeItem(key)
    })
  }
}

/**
 * SSR-safe hook for persisting state to sessionStorage.
 *
 * During SSR, the parser is called with undefined and should return a default.
 * On client mount, the actual value is loaded from sessionStorage.
 *
 * This approach prevents "e.getItem is not a function" errors that occur when
 * trying to access storage during server-side rendering.
 */
export const useStateStorage = <T>({ key, parser }: UseStateStorageParams<T>) => {
  // Initialize with parser(undefined) - parser should handle this case
  const [rawState, setRawState] = useState<unknown>(undefined)
  const [isHydrated, setIsHydrated] = useState(false)

  // Load from storage on mount (client-side only)
  useEffect(() => {
    if (!isClientSide()) {
      return
    }

    try {
      const storedValue = window.sessionStorage.getItem(key)
      if (storedValue !== null) {
        setRawState(JSON.parse(storedValue))
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error loading from sessionStorage key "${key}":`, error)
    }
    setIsHydrated(true)
  }, [key])

  const setStorage = useCallback(
    (data: unknown) => {
      setRawState(data)
      if (isClientSide()) {
        try {
          if (data === undefined || data === null) {
            window.sessionStorage.removeItem(key)
          } else {
            window.sessionStorage.setItem(key, JSON.stringify(data))
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Error saving to sessionStorage key "${key}":`, error)
        }
      }
    },
    [key]
  )

  const removeStorage = useCallback(() => {
    setRawState(undefined)
    if (isClientSide()) {
      try {
        window.sessionStorage.removeItem(key)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error removing from sessionStorage key "${key}":`, error)
      }
    }
  }, [key])

  // Parse the state (parser should handle undefined for SSR case)
  const storage = parser(rawState)

  return { storage, setStorage, removeStorage, isHydrated } as const
}
